import { forwardRef } from 'react';

import { cn } from '../utils';
import { spinnerGlyphBoxRecipe, spinnerLabelRecipe, spinnerRecipe } from './Spinner.recipe';
import { SpinnerGlyph } from './SpinnerGlyph';

import type { SpinnerProps } from './types';

/**
 * Design System 2.0 spinner: something is loading and the wait has no known length.
 *
 * When the progress IS known, use `ProgressBar`. When the shape of the content is known in advance,
 * use `Skeleton`. Inside a control, `Button` and `IconButton` take `loading`; a status that is still
 * working takes `Tag`'s `loading`.
 *
 * It is its own announcement: a `role="status"` that carries the label, so it does not need a busy
 * ancestor. The ring is decoration and is hidden from assistive technology. Colour is the secondary
 * foreground, never the accent; pass a `className` with a text colour when it sits on a filled
 * surface.
 *
 * Every loading state ends in content, an empty state or an error. After about ten seconds, say so
 * and offer Try again and Cancel; that is the screen's job, not this component's.
 */
export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(
  ({ label, showLabel = false, size = 'md', delayed = false, className, ...rest }, ref) => (
    <span
      ref={ref}
      {...rest}
      className={cn(spinnerRecipe({ delayed }), className)}
      // After the spread, and left out of the prop type: these are what make it a status. The
      // `undefined`s are deliberate; they strip anything a caller forced through a cast.
      aria-busy={undefined}
      aria-hidden={undefined}
      aria-live="polite"
      role="status"
    >
      <span className={spinnerGlyphBoxRecipe({ size })}>
        <SpinnerGlyph />
      </span>
      <span className={spinnerLabelRecipe({ size, visible: showLabel })}>{label}</span>
    </span>
  )
);
